if (!customElements.get('date-picker')) {
  class Day {
    static get names() {
      return ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
    }
  }

  class Month {
    constructor(month, year) {
      this.month = month;
      this.year = year;
    }

    decrement() {
      this.month--;
    }

    increment() {
      this.month++;
    }

    get value() {
      return this.month;
    }

    get numberOfDays() {
      if (
        this.month === 1 ||
        this.month === 3 ||
        this.month === 5 ||
        this.month === 7 ||
        this.month === 8 ||
        this.month === 10 ||
        this.month === 12
      ) {
        return 31;
      } else if (this.month === 4 || this.month === 6 || this.month === 9 || this.month === 11) {
        return 30;
      } else if (this.month === 2 && this.year.isLeapYear) {
        return 29;
      } else if (this.month === 2 && !this.year.isLeapYear) {
        return 28;
      }
    }

    static get names() {
      return [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
      ];
    }
  }

  class Year {
    constructor(year) {
      this.year = year;
    }

    decrement() {
      this.year--;
    }

    increment() {
      this.year++;
    }

    get isLeapYear() {
      return (this.year % 4 === 0 && this.year % 100 !== 0) || this.year % 400 === 0;
    }

    get value() {
      return this.year;
    }
  }

  customElements.define(
    'date-picker',
    class Datepicker extends HTMLElement {
      constructor() {
        super();
        // Regardless of sundayFirst value, set monday as first, sunday as last, always:
        this.sundayFirst = false;
        this.persistOnSelect = false;
        this.longPressThreshold = 500;
        this.longPressInterval = 150;
        this.initDate = null;
        this.ignoreOnFocus = false;
        this.showCloseIcon = false;
        this._inputStrIsValidDate = false;
        this._longPressIntervalIds = [];
        this._longPressTimerIds = [];
        this._availableDays = [];
        this._hasSelection = false;
      }

      static get observedAttributes() {
        return ['init-date', 'ignore-on-focus', 'sunday-first', 'persist-on-select', 'show-close-icon'];
      }

      attributeChangedCallback(name, oldValue, newValue) {
        if (name === 'init-date') {
          this.initDate = newValue;
        } else if (name === 'ignore-on-focus') {
          this.ignoreOnFocus = true;
        } else if (name === 'sunday-first') {
          this.sundayFirst = true;
        } else if (name === 'persist-on-select') {
          this.persistOnSelect = true;
        } else if (name === 'show-close-icon') {
          this.showCloseIcon = true;
        }
      }

      connectedCallback() {
        setTimeout(() => {
          this.init();
        }, 0); // https://stackoverflow.com/questions/58676021/accessing-custom-elements-child-element-without-using-slots-shadow-dom
      }

      init() {
        this.textInputElement = this.querySelector('input');
        if (this.textInputElement === null) {
          return;
        }
        const mainContainer = document.createElement('div');
        if (this.container) {
          this.container.remove();
        }
        this.container = this.appendChild(mainContainer); // The returned value is the appended child

        const template = document.createElement('template');
        template.innerHTML = this.template;

        this.container.appendChild(this.textInputElement);
        this.container.appendChild(template.content);

        this.currentMonthTitle = this.querySelector('#calendar-ctrl-current-month');
        this.calendarContainer = this.querySelector('#calendar-container');
        this.dateObj = new Date();
        let obj;

        if (this.initDate !== null) {
          obj = this._parseAndValidateInputStr(this.initDate);
          if (obj.valid) {
            this.dateObj = new Date(obj.year, obj.month, obj.day);
            this._inputStrIsValidDate = true;
            this.textInputElement.value = this._returnDateString(this.dateObj);
          } else if (this.initDate === 'current') {
            this._inputStrIsValidDate = true;
            this.textInputElement.value = this._returnDateString(this.dateObj);
          }
        } else {
          obj = this._parseAndValidateInputStr(this.textInputElement.value);
          if (obj.valid) {
            this.dateObj = new Date(obj.year, obj.month, obj.day);
            this._inputStrIsValidDate = true;
          } else {
            this._inputStrIsValidDate = false;
          }
        }
        this.initDate = null;

        this.displayedYear = new Year(this.dateObj.getFullYear());
        this.displayedMonth = new Month(this.dateObj.getMonth(), this.displayedYear);

        this._populateDayNames();
        this._addHeaderEventHandlers();
        this._renderCalendar();

        if (!this.ignoreOnFocus) {
          this.textInputElement.onfocus = this._inputOnFocusHandler;
          this.textInputElement.onfocus = this.textInputElement.onfocus.bind(this);
        }

        this.textInputElement.oninput = this._inputOnInputHandler;
        this.textInputElement.oninput = this.textInputElement.oninput.bind(this);

        this.textInputElement.onblur = this._blurHandler;
        this.textInputElement.onblur = this.textInputElement.onblur.bind(this);

        this.calendarContainer.onblur = this._blurHandler;
        this.calendarContainer.onblur = this.calendarContainer.onblur.bind(this);
      }

      setFocusOnCal() {
        if (this.calendarContainer) {
          this.calendarContainer.style.display = 'block';
          this.calendarContainer.focus();
        }
      }

      getDateFromString(string) {
        const [year, month, day] = string.split('-');

        // We do the date this way so the time is 00:00. Doing new Date(dayDate.date) sets it at 01:00
        return new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
      }

      setAvailableDays(availableDays) {
        this._availableDays = availableDays;
        this._renderCalendar();
      }

      _dayClickedEventHandler(event) {
        const day = parseInt(event.target.textContent, 10);
        const date = this.getDateFromString(event.currentTarget.dataset.date);
        if (!this.availableDaysTimestamps.includes(date.getTime())) return;

        this._hasSelection = true;

        this._inputStrIsValidDate = true;
        this._setNewDateValue(day, this.displayedMonth.value, this.displayedYear.value);
        this.textInputElement.value = this._returnDateString(this.dateObj);
        this.textInputElement.dispatchEvent(new CustomEvent('dateselect', { bubbles: true }));
        this._renderCalendar();
        if (!this.persistOnSelect) {
          this._hideCalendar();
        }
      }

      _hideCalendar() {
        document.activeElement.blur();
      }

      _calKeyDownEventHandler(event) {
        if (event.key === 'Enter') {
          this._dayClickedEventHandler(event);
        }
      }

      _blurHandler() {
        // When the input element loses focus due to click on calendar__container, new focus won't be
        // directly set to calendar__container, it is set to body. After calendar__container onclick, focus will
        // be on body unless following delay is introduced:
        setTimeout(() => {
          checkActiveElement(this);
        }, 0);

        function checkActiveElement(ctx) {
          if (
            !(
              document.activeElement.id === 'calendar__container' ||
              document.activeElement.classList.contains('calendar__ctrl') ||
              document.activeElement.classList.contains('calendar__day') ||
              document.activeElement.isSameNode(ctx.textInputElement)
            )
          ) {
            ctx._mouseUpEventHandler();
            if (!ctx._inputStrIsValidDate) {
              ctx.textInputElement.dispatchEvent(new Event('invalid'));
            }
          }
        }
      }

      _addHeaderEventHandlers() {
        const entries = this.calendarContainer.querySelectorAll('.calendar__ctrl').entries();
        let entry = entries.next();
        while (entry.done === false) {
          entry.value[1].tabIndex = 0;
          entry.value[1].onblur = this._blurHandler;
          entry.value[1].onblur = entry.value[1].onblur.bind(this);
          entry.value[1].onclick = this._controlKeyDownEventHandler;
          entry.value[1].onclick = entry.value[1].onclick.bind(this);
          entry.value[1].onkeydown = this._controlKeyDownEventHandler;
          entry.value[1].onkeydown = entry.value[1].onkeydown.bind(this);
          entry.value[1].onmousedown = this._mouseDownEventHandler;
          entry.value[1].onmousedown = entry.value[1].onmousedown.bind(this);
          entry.value[1].onmouseup = this._mouseUpEventHandler;
          entry.value[1].onmouseup = entry.value[1].onmouseup.bind(this);
          entry.value[1].onmouseleave = this._mouseUpEventHandler;
          entry.value[1].onmouseleave = entry.value[1].onmouseleave.bind(this);
          entry.value[1].ontouchstart = this._mouseDownEventHandler;
          entry.value[1].ontouchstart = entry.value[1].ontouchstart.bind(this);
          entry.value[1].ontouchend = this._mouseUpEventHandler;
          entry.value[1].ontouchend = entry.value[1].ontouchend.bind(this);
          entry.value[1].ontouchcancel = this._mouseUpEventHandler;
          entry.value[1].ontouchcancel = entry.value[1].ontouchcancel.bind(this);
          entry = entries.next();
        }
      }

      _startLongPressAction(event) {
        this._longPressIntervalIds.push(
          setInterval(() => {
            this._controlKeyDownEventHandler(event);
          }, this.longPressInterval),
        );
        this.querySelector(`#${event.target.id}`).onclick = () => {
          this._onClickHandlerAfterLongPress(event, this);
        };
      }

      // For better UX, after long press, onclick must be discarded once,
      // thus do nothing with the event and set clickhandler back to the real one:
      _onClickHandlerAfterLongPress(event, ctx) {
        ctx.querySelector(`#${event.target.id}`).onclick = ctx._controlKeyDownEventHandler;
        ctx.querySelector(`#${event.target.id}`).onclick = ctx.querySelector(`#${event.target.id}`).onclick.bind(ctx);
      }

      _mouseDownEventHandler(event) {
        this._longPressTimerIds.push(
          setTimeout(() => {
            this._startLongPressAction(event);
          }, this.longPressThreshold),
        );
      }

      _mouseUpEventHandler() {
        this._longPressTimerIds.forEach(clearTimeout);
        this._longPressTimerIds = [];
        this._longPressIntervalIds.forEach(clearInterval);
        this._longPressIntervalIds = [];
      }

      _parseAndValidateInputStr(str) {
        const obj = {};
        let day, month, year;
        const value = str.match(/^\s*(\d{1,2})\.(\d{1,2})\.(\d\d\d\d)\s*$/);
        if (value === null) {
          obj.valid = false;
        } else {
          day = Number(value[1]);
          month = Number(value[2]);
          year = Number(value[3]);
          if (this._dateIsValid(day, month, year)) {
            obj.valid = true;
            obj.day = day;
            obj.month = month - 1;
            obj.year = year;
          } else {
            obj.valid = false;
          }
        }
        return obj;
      }

      _inputOnInputHandler() {
        const obj = this._parseAndValidateInputStr(this.textInputElement.value);
        if (obj.valid) {
          this._inputStrIsValidDate = true;
          this._setNewDateValue(obj.day, obj.month, obj.year);
          this.displayedYear = new Year(obj.year);
          this.displayedMonth = new Month(obj.month, this.displayedYear);
          this.textInputElement.dispatchEvent(new CustomEvent('dateselect'));
          this._renderCalendar();
        } else {
          this._inputStrIsValidDate = false;
        }
      }

      _dateIsValid(day, month, year) {
        if (month < 1 || month > 12) {
          return false;
        }
        const lastDayOfTheMonth = this._daysInMonth(month, year);

        if (day < 1 || day > lastDayOfTheMonth) {
          return false;
        }

        return true;
      }

      _controlKeyDownEventHandler(event) {
        if (event.key === 'Enter' || event.type !== 'keydown') {
          switch (event.target.id) {
            case 'calendar-ctrl-prev-Year':
              this._showPrevYear();
              break;
            case 'calendar-ctrl-next-Year':
              this._showNextYear();
              break;
            case 'calendar-ctrl-prev-month':
              this._showPrevMonth();
              break;
            case 'calendar-ctrl-next-month':
              this._showNextMonth();
              break;
            case 'calendar-sctrl-hide-calendar':
              this._hideCalendar();
              break;
          }
        }
      }

      _inputOnFocusHandler() {
        this._inputOnInputHandler();
        this.calendarContainer.style.display = 'block';
      }

      _showNextYear() {
        this.displayedYear.increment();
        this._renderCalendar();
      }

      _showPrevYear() {
        this.displayedYear.decrement();
        this._renderCalendar();
      }

      _showNextMonth() {
        if (this.displayedMonth.value === 11) {
          this.displayedYear.increment();
          this.displayedMonth = new Month(0, this.displayedYear);
        } else {
          this.displayedMonth.increment();
        }
        this._renderCalendar();
      }

      _showPrevMonth() {
        if (this.displayedMonth.value === 0) {
          this.displayedYear.decrement();
          this.displayedMonth = new Month(11, this.displayedYear);
        } else {
          this.displayedMonth.decrement();
        }
        this._renderCalendar();
      }

      _renderCalendar() {
        const tempDate = new Date(this.displayedYear.value, this.displayedMonth.value);
        tempDate.setDate(1);

        this.currentMonthTitle.textContent = this.currentMonth;

        const dayNumbers = [];
        const adjacentMonthDays = [];
        this._generateDayArray(tempDate, dayNumbers, adjacentMonthDays);
        let entries = this.calendarContainer.querySelectorAll('.calendar__day').entries();
        let entry = entries.next();
        const monthOptions = {
          PREVIOUS_MONTH: 'PREVIOUS_MONTH',
          CURRENT_MONTH: 'CURRENT_MONTH',
          NEXT_MONTH: 'NEXT_MONTH',
        };
        let monthState = monthOptions.PREVIOUS_MONTH;
        while (entry.done === false) {
          const isAdjacentDay = adjacentMonthDays[entry.value[0]];

          if (!isAdjacentDay && monthState === monthOptions.PREVIOUS_MONTH) {
            monthState = monthOptions.CURRENT_MONTH;
          } else if (isAdjacentDay && monthState === monthOptions.CURRENT_MONTH) {
            monthState = monthOptions.NEXT_MONTH;
          }

          const date = (() => {
            if (monthState === monthOptions.PREVIOUS_MONTH) {
              return new Date(this.displayedYear.value, this.displayedMonth.value - 1, dayNumbers[entry.value[0]]);
            } else if (monthState === monthOptions.CURRENT_MONTH) {
              return new Date(this.displayedYear.value, this.displayedMonth.value, dayNumbers[entry.value[0]]);
            } else if (monthState === monthOptions.NEXT_MONTH) {
              return new Date(this.displayedYear.value, this.displayedMonth.value + 1, dayNumbers[entry.value[0]]);
            }
          })();
          const element = entry.value[1];

          element.classList.remove('calendar__day--adjacent');
          element.classList.remove('calendar__day--selected');
          element.classList.remove('calendar-hidden-row');
          element.classList.remove('calendar-day-style');
          element.classList.remove('calendar__day--unavailable');
          element.onclick = null;
          element.onblur = null;
          element.onkeydown = null;

          if (isAdjacentDay) {
            element.classList.add('calendar__day--adjacent');
          } else {
            element.classList.add('calendar-day-style');

            if (!this.availableDaysTimestamps.includes(date.getTime())) {
              element.classList.add('calendar__day--unavailable');
            }
          }

          element.innerHTML = `<span class="calendar__day-text font-desktop-ui-14 font-mobile-ui-14">${dayNumbers[entry.value[0]]}</span>`;
          element.dataset.date = this._returnDateString(date);

          if (
            this.displayedMonth.value === this.dateObj.getMonth() &&
            this.displayedYear.value === this.dateObj.getFullYear() &&
            dayNumbers[entry.value[0]] === this.dateObj.getDate() &&
            !adjacentMonthDays[entry.value[0]] &&
            this._hasSelection
          ) {
            element.classList.add('calendar__day--selected');
          }

          if (!adjacentMonthDays[entry.value[0]]) {
            element.onclick = this._dayClickedEventHandler;
            element.onclick = element.onclick.bind(this);
            element.onkeydown = this._calKeyDownEventHandler;
            element.onkeydown = element.onkeydown.bind(this);
            element.tabIndex = 0;
            element.onblur = this._blurHandler;
            element.onblur = element.onblur.bind(this);
          } else {
            element.removeAttribute('tabindex');
          }
          entry = entries.next();
        }

        // checking if last (=lowest) row of days are all adjacent month days:
        const lastSeven = adjacentMonthDays.slice(35, 42);
        if (lastSeven.every((x) => x === true)) {
          entries = this.calendarContainer.querySelectorAll('.calendar__day').entries();
          entry = entries.next();
          while (entry.done === false) {
            if (entry.value[0] > 34) {
              entry.value[1].classList.add('calendar-hidden-row');
            }
            entry = entries.next();
          }
        }
      }

      _setNewDateValue(day, month, year) {
        day = Number(day);
        month = Number(month);
        year = Number(year);
        if (
          day !== this.dateObj.getDate() ||
          month !== this.dateObj.getMonth() ||
          year !== this.dateObj.getFullYear()
        ) {
          // Order is important, always set year first:
          this.dateObj.setFullYear(year);
          // Do not use setDate here:
          // this.dateObj.setDate(day) <--
          // https://stackoverflow.com/questions/14680396/the-date-getmonth-method-has-bug Use
          // setMonth with 2 params instead:
          this.dateObj.setMonth(month, day);
        }
      }

      _returnDateString(date) {
        const year = date.getFullYear();
        const month = date.getMonth() + 1;
        const day = date.getDate();
        return `${year}-${month}-${day}`;
      }

      _populateDayNames() {
        const dayNameArray = this.dayNames.slice();

        if (this.sundayFirst) {
          dayNameArray.pop();
          dayNameArray.unshift(this.dayNames[6]);
        }
        const entries = this.calendarContainer.querySelectorAll('.calendar__day-name').entries();
        let entry = entries.next();
        while (entry.done === false) {
          entry.value[1].innerHTML = dayNameArray[entry.value[0]];
          entry = entries.next();
        }
      }

      _generateDayArray(date, dayArray, adjacentMonthDaysArray) {
        let index;
        const dateDay = date.getDay();
        const dateMonth = date.getMonth() + 1;
        const dateYear = date.getFullYear();
        const daysInMonth = this._daysInMonth(dateMonth, dateYear);

        date.setDate(date.getDate() - 1);
        const prevMonth = date.getMonth() + 1;
        const prevMonthYear = date.getFullYear();
        let daysInPrevMonth = this._daysInMonth(prevMonth, prevMonthYear);

        // prev month day filling:
        if (this.sundayFirst) {
          for (index = 0; index < dateDay; index++) {
            dayArray.unshift(daysInPrevMonth);
            daysInPrevMonth--;
            adjacentMonthDaysArray.push(true);
          }
        } else {
          if (dateDay === 0) {
            for (index = 0; index < 6; index++) {
              dayArray.unshift(daysInPrevMonth);
              daysInPrevMonth--;
              adjacentMonthDaysArray.push(true);
            }
          } else {
            for (index = 0; index < dateDay - 1; index++) {
              dayArray.unshift(daysInPrevMonth);
              daysInPrevMonth--;
              adjacentMonthDaysArray.push(true);
            }
          }
        }

        // current month day filling:
        for (index = 0; index < daysInMonth; index++) {
          dayArray.push(index + 1);
          adjacentMonthDaysArray.push(false);
        }

        // next month day filling:
        const numberOfNextMonthDays = 42 - dayArray.length;
        for (index = 0; index < numberOfNextMonthDays; index++) {
          dayArray.push(index + 1);
          adjacentMonthDaysArray.push(true);
        }
      }

      _isItLeapYear(year) {
        return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
      }

      _daysInMonth(month, year) {
        if (month === 1 || month === 3 || month === 5 || month === 7 || month === 8 || month === 10 || month === 12) {
          return 31;
        } else if (month === 4 || month === 6 || month === 9 || month === 11) {
          return 30;
        } else if (month === 2 && this._isItLeapYear(year)) {
          return 29;
        } else if (month === 2 && !this._isItLeapYear(year)) {
          return 28;
        }
      }

      get availableDaysTimestamps() {
        return this._availableDays.map((date) => date.getTime());
      }

      get dayNames() {
        return Day.names;
      }

      get monthNames() {
        return Month.names;
      }

      get currentMonth() {
        return this.monthNames[this.displayedMonth.value];
      }

      get nextMonth() {
        const monthIndex = this.displayedMonth.value + 1;

        return monthIndex > 11 ? this.monthNames[0] : this.monthNames[monthIndex];
      }

      get template() {
        return `
          <style>
          .calendar-hidden-row {
            display:none;
          }
          </style>
          <div id="calendar-container" class="calendar__container calendar" tabindex="0">
            <div id="calendar-header" class="calendar__header">
              <p
                id="calendar-ctrl-prev-month"
                class="calendar__ctrl button"
                role="button"
                aria-label="Previous month
              ">
                <svg
                  class="calendar__icon icon icon--discolsure-left-c"
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  fill="none"
                >
                  <path d="M7.48 5.949A139.448 139.448 0 0 0 5.587 8c0 .031 3.783 4.081 3.805 4.073.021-.008.698-.637.708-.658.004-.008-.707-.78-1.58-1.715L6.933 8 8.52 6.3a89.222 89.222 0 0 0 1.58-1.715 18.31 18.31 0 0 0-.708-.658c-.01-.004-.871.906-1.912 2.022" fill-rule="evenodd" fill="currentColor"/>
                </svg>
                
              </p>
              <p id="calendar-ctrl-current-month" class="font-desktop-ui-16 font-mobile-ui-16"></p>
              <p
                id="calendar-ctrl-next-month"
                class="calendar__ctrl button"
                role="button"
                aria-label="Next month"
              >
                <svg
                  class="calendar__icon icon icon--disclosure-right-c"
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                >
                  <path d="M6.25 4.25L9.75 8L6.25 11.75" stroke="currentColor"/>
                </svg>
              </p>
            </div>

            <div id="calendar-grid-day-name" class="calendar__grid calendar__grid--day-name">
              <div class="calendar__day-name calendar__name font-desktop-ui-14 font-mobile-ui-14"></div>
              <div class="calendar__day-name calendar__name font-desktop-ui-14 font-mobile-ui-14"></div>
              <div class="calendar__day-name calendar__name font-desktop-ui-14 font-mobile-ui-14"></div>
              <div class="calendar__day-name calendar__name font-desktop-ui-14 font-mobile-ui-14"></div>
              <div class="calendar__day-name calendar__name font-desktop-ui-14 font-mobile-ui-14"></div>
              <div class="calendar__day-name calendar__name font-desktop-ui-14 font-mobile-ui-14"></div>
              <div class="calendar__day-name calendar__name font-desktop-ui-14 font-mobile-ui-14"></div>
            </div>

            <div id="calendar-grid-day" class="calendar__grid calendar__grid--day">
              <div class="calendar__day font-desktop-ui-14 font-mobile-ui-14"></div>
              <div class="calendar__day font-desktop-ui-14 font-mobile-ui-14"></div>
              <div class="calendar__day font-desktop-ui-14 font-mobile-ui-14"></div>
              <div class="calendar__day font-desktop-ui-14 font-mobile-ui-14"></div>
              <div class="calendar__day font-desktop-ui-14 font-mobile-ui-14"></div>
              <div class="calendar__day font-desktop-ui-14 font-mobile-ui-14"></div>
              <div class="calendar__day font-desktop-ui-14 font-mobile-ui-14"></div>
              <div class="calendar__day font-desktop-ui-14 font-mobile-ui-14"></div>
              <div class="calendar__day font-desktop-ui-14 font-mobile-ui-14"></div>
              <div class="calendar__day font-desktop-ui-14 font-mobile-ui-14"></div>
              <div class="calendar__day font-desktop-ui-14 font-mobile-ui-14"></div>
              <div class="calendar__day font-desktop-ui-14 font-mobile-ui-14"></div>
              <div class="calendar__day font-desktop-ui-14 font-mobile-ui-14"></div>
              <div class="calendar__day font-desktop-ui-14 font-mobile-ui-14"></div>
              <div class="calendar__day font-desktop-ui-14 font-mobile-ui-14"></div>
              <div class="calendar__day font-desktop-ui-14 font-mobile-ui-14"></div>
              <div class="calendar__day font-desktop-ui-14 font-mobile-ui-14"></div>
              <div class="calendar__day font-desktop-ui-14 font-mobile-ui-14"></div>
              <div class="calendar__day font-desktop-ui-14 font-mobile-ui-14"></div>
              <div class="calendar__day font-desktop-ui-14 font-mobile-ui-14"></div>
              <div class="calendar__day font-desktop-ui-14 font-mobile-ui-14"></div>
              <div class="calendar__day font-desktop-ui-14 font-mobile-ui-14"></div>
              <div class="calendar__day font-desktop-ui-14 font-mobile-ui-14"></div>
              <div class="calendar__day font-desktop-ui-14 font-mobile-ui-14"></div>
              <div class="calendar__day font-desktop-ui-14 font-mobile-ui-14"></div>
              <div class="calendar__day font-desktop-ui-14 font-mobile-ui-14"></div>
              <div class="calendar__day font-desktop-ui-14 font-mobile-ui-14"></div>
              <div class="calendar__day font-desktop-ui-14 font-mobile-ui-14"></div>
              <div class="calendar__day font-desktop-ui-14 font-mobile-ui-14"></div>
              <div class="calendar__day font-desktop-ui-14 font-mobile-ui-14"></div>
              <div class="calendar__day font-desktop-ui-14 font-mobile-ui-14"></div>
              <div class="calendar__day font-desktop-ui-14 font-mobile-ui-14"></div>
              <div class="calendar__day font-desktop-ui-14 font-mobile-ui-14"></div>
              <div class="calendar__day font-desktop-ui-14 font-mobile-ui-14"></div>
              <div class="calendar__day font-desktop-ui-14 font-mobile-ui-14"></div>
              <div class="calendar__day font-desktop-ui-14 font-mobile-ui-14"></div>
              <div class="calendar__day font-desktop-ui-14 font-mobile-ui-14"></div>
              <div class="calendar__day font-desktop-ui-14 font-mobile-ui-14"></div>
              <div class="calendar__day font-desktop-ui-14 font-mobile-ui-14"></div>
              <div class="calendar__day font-desktop-ui-14 font-mobile-ui-14"></div>
              <div class="calendar__day font-desktop-ui-14 font-mobile-ui-14"></div>
              <div class="calendar__day font-desktop-ui-14 font-mobile-ui-14"></div>
            </div>
          </div>`;
      }
    },
  );
}
